import React from "react";
import {extractLocaleString} from "./shared";
import PropTypes from "prop-types";
import {
  IndexPageBlockBlurRowStyled,
  IndexPageBlockButtonContainerStyled,
  IndexPageBlockButtonObjectStyled,
  IndexPageBlockButtonStyled,
  IndexPageBlockButtonTextStyled,
  IndexPageBlockFadeCircleCenterRowStyled,
  IndexPageBlockFadeCircleRowStyled,
  IndexPageBlockFadeRowStyled,
  IndexPageBlockShiftRowStyled,
  IndexPageBlurBlockButtonStyled,
  IndexPageFadeBlockButtonStyled
} from "./IndexPage.style";
import {addAssetVersionToUrl} from "../../utils/etc";
import TouchEffect from "../../components/TouchEffect/TouchEffect";
import Loader from "../../components/Loader/Loader";

export default class BlockView extends React.Component {
  state = {
    isShown: false,
    isLoaded: false,
    renderProps: null
  };

  intersectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.setState({isShown: true});
      }
    });
  }, {
    root: null,
    threshold: 0.01,
  });

  componentDidMount() {
    this.intersectionObserver.observe(this.blockRef);
  }

  componentWillUnmount() {
    this.intersectionObserver.disconnect();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.state.isShown && !prevState.isShown) {
      this.startLoadImage();
    }
  }

  startLoadImage = () => {
    const assets = this.props.block.assets;

    const image = new Image();
    image.onload = () => {
      const assets = [this.props.block.assets];

      try {
        if (this.props.block.extra.assets) {
          const extraAssets = JSON.parse(this.props.block.extra.assets);
  
          if (window.clientConfig.isAnyAndroid && Array.isArray(extraAssets.android)) {
            assets.unshift(extraAssets.android);
          }
  
          if (window.clientConfig.isAnyIOS && Array.isArray(extraAssets.ios)) {
            assets.unshift(extraAssets.ios);
          }
        }
      } catch (e) {
        console.error("Failed parse JSON with assets for block " + this.props.block.name, e);
      }
  
      const renderProps = {
        title: extractLocaleString(this.props.block.title),
        assetsUrls: assets.first((assetUrl) => {
          return assetUrl.includes("http") ? addAssetVersionToUrl(assetUrl) : assetUrl;
        }),
      };

      this.setState({isLoaded: true, renderProps: renderProps});
    }

    image.src = assets.first();
  }

  handleRef = (ref) => {
    this.blockRef = ref;
    this.props.blockRefHandler(ref, this.props.block, this.props.gender);
  }

  handleClick = () => {
    this.props.onClick && this.props.onClick(this.props.block);
  };

  render() {
    return <IndexPageBlockButtonContainerStyled 
      ref={this.handleRef} 
      isDesktop={window.clientConfig.isWebDesktop}
      >
      {this.props.block.effect.animation === "shift" && this.renderShiftWide(this.state.renderProps)}
      {this.props.block.effect.animation === "blur" && this.renderBlurWide(this.state.renderProps)}
      {this.props.block.effect.animation === "fade" && this.props.block.effect.direction === "vertical" && this.props.block.type === "wide" && this.renderFadeWide(this.state.renderProps)}
      {this.props.block.effect.animation === "fade" && this.props.block.effect.direction === "circle" && this.props.block.type === "wide" && this.renderFadeCircleWide(this.state.renderProps)}
      {this.props.block.effect.animation === "fade" && this.props.block.effect.direction === "circle-center" && this.props.block.type === "wide" && this.renderFadeCircleCenterWide(this.state.renderProps)}
      {this.props.block.type === "normal" && this.props.block.effect.direction === "toggle" && this.renderFadeDefault(this.state.renderProps)}
      {this.props.block.type !== "wide" && (this.props.block.effect.animation !== "shift" || this.props.block.effect.animation !== "blur" || this.props.block.effect.animation !== "fade") && this.props.block.effect.direction !== "toggle" && this.renderDefault(this.state.renderProps)}
    </IndexPageBlockButtonContainerStyled>
  }

  renderFadeCircleCenterWide = (props) => {
    if (!this.state.isLoaded) {
      return <IndexPageBlockButtonStyled
        type={this.props.block.type}
        effectAnimation={this.props.block.effect.animation}
        effectDirection={this.props.block.effect.direction}
        onClick={this.handleClick}
      ><Loader /></IndexPageBlockButtonStyled>;
    }
    
    const chunks = [];

    for (let i = 0; i < props.assetsUrls.length; i++) {
      if (i === 0) {
        chunks.push([props.assetsUrls[i + 1], props.assetsUrls[i]]);
      } else if (i === props.assetsUrls.length - 1) {
        chunks.push([props.assetsUrls.first(), props.assetsUrls[i]]);
      } else {
        chunks.push([props.assetsUrls[i + 1], props.assetsUrls[i]]);
      }
    }

    return <IndexPageBlockButtonStyled
      type={this.props.block.type}
      effectAnimation={this.props.block.effect.animation}
      effectDirection={this.props.block.effect.direction}
      onClick={this.handleClick}
      isDesktop={window.clientConfig.isWebDesktop}
    >
      {this.props.block.extra.is_pro && <p className="pro-label">Pro</p>}
      {chunks.map((item, index) => {
        return <IndexPageBlockFadeCircleCenterRowStyled
          className="fade-row"
          isDesktop={window.clientConfig.isWebDesktop}
          style={{"--animation-duration": `${props.assetsUrls.length * 3}s`, "--animation-delay": `${index * 3}s`, "--animation-delay-mask": `${index * 3 + 0.5}s`}}
          keyframes={`
            ${100 / chunks.length - 0.00000001}% { opacity: 1;}
            ${100 / chunks.length}% { opacity: 0;}
          `}
          keyframesScale={`
            ${100 / chunks.length * 0.5}% { scale: 1;}
          `}
          keyframesMask={`
            ${100 / chunks.length * 0.1}% { 
              mask-position: center;
              mask-size: 300%;
            }
            ${100 / chunks.length * 0.25}% { 
              mask-position: center;
              mask-size: 150%;
            }
            ${100 / chunks.length * 0.4}% { 
              mask-position: center;
              mask-size: 0%;
            }
            ${100 / chunks.length - 0.01}% { 
              mask-position: center;
              mask-size: 0%;
            }
            ${100 / chunks.length}% { 
              mask-position: center;
              mask-size: 0%;
            }
          `}
          key={index}
        >
          <img alt="" src={item.first()} />
          <div
            className="mask"
          >
            <img alt="" src={item.last()} />
          </div>
        </IndexPageBlockFadeCircleCenterRowStyled>;
      })}
      <IndexPageBlockButtonTextStyled>
        {props.title}
      </IndexPageBlockButtonTextStyled>
      <TouchEffect zIndex={10} />
    </IndexPageBlockButtonStyled>;
  };

  renderFadeCircleWide = (props) => {
    if (!this.state.isLoaded) {
      return <IndexPageBlockButtonStyled
        type={this.props.block.type}
        effectAnimation={this.props.block.effect.animation}
        effectDirection={this.props.block.effect.direction}
        onClick={this.handleClick}
      ><Loader /></IndexPageBlockButtonStyled>;
    }

    const chunkSize = 2;
    const chunks = [];
    for (let i = 0; i < props.assetsUrls.length; i += chunkSize) {
      chunks.push(props.assetsUrls.slice(i, i + chunkSize));
    }

    return <IndexPageBlockButtonStyled
      type={this.props.block.type}
      effectAnimation={this.props.block.effect.animation}
      effectDirection={this.props.block.effect.direction}
      onClick={this.handleClick}
    >
      {this.props.block.extra.is_pro && <p className="pro-label">Pro</p>}
      {chunks.map((item, index) => {
        return <IndexPageBlockFadeCircleRowStyled
          className="fade-row"
          isDesktop={window.clientConfig.isWebDesktop}
          style={{"--animation-duration": `${props.assetsUrls.length * 1.5}s`, "--animation-delay": `${index * 3}s`, "--animation-delay-mask": `${index * 3 + 0.5}s`}}
          keyframes={`
            ${100 / chunks.length - 0.01}% { opacity: 1;}
            ${100 / chunks.length}% { opacity: 0;}
          `}
          keyframesMask={`
            ${100 / chunks.length * 0.4}% { 
              mask-position: center -200px;
              mask-size: 200%;
            }
            ${100 / chunks.length * 0.7}% { 
              mask-position: center -300px;
              mask-size: 300%;
            }
            ${100 / chunks.length - 0.01}% { 
              mask-position: center -300px;
              mask-size: 300%;
            }
            ${100 / chunks.length}% { 
              mask-position: center 10px;
              mask-size: 0%;
            }
          `}
          key={index}
        >
          <img alt="" src={item.first()} />
          <div
            className="mask"
          >
            <img alt="" src={item.last()} />
          </div>
        </IndexPageBlockFadeCircleRowStyled>;
      })}
      <IndexPageBlockButtonTextStyled>
        {props.title}
      </IndexPageBlockButtonTextStyled>
      <TouchEffect zIndex={10} />
    </IndexPageBlockButtonStyled>;
  };

  renderFadeWide = (props) => {
    if (!this.state.isLoaded) {
      return <IndexPageBlockButtonStyled
        type={this.props.block.type}
        effectAnimation={this.props.block.effect.animation}
        effectDirection={this.props.block.effect.direction}
        onClick={this.handleClick}
      ><Loader /></IndexPageBlockButtonStyled>;
    }

    const chunkSize = 2;
    const chunks = [];
    for (let i = 0; i < props.assetsUrls.length; i += chunkSize) {
      chunks.push(props.assetsUrls.slice(i, i + chunkSize));
    }
    return <IndexPageBlockButtonStyled
      type={this.props.block.type}
      effectAnimation={this.props.block.effect.animation}
      effectDirection={this.props.block.effect.direction}
      onClick={this.handleClick}
    >
      {this.props.block.extra.is_pro && <p className="pro-label">Pro</p>}
      {chunks.map((item, index) => {
        return <IndexPageBlockFadeRowStyled
          className="fade-row"
          isDesktop={window.clientConfig.isWebDesktop}
          style={{
            "--animation-duration": `${props.assetsUrls.length * 1.5}s`,
            "--animation-delay": `${index * 3}s`,
            "--animation-delay-mask": `${index * 3 + 0.9}s`
          }}
          keyframes={`
            ${100 / chunks.length - 0.001}% { opacity: 1;}
            ${100 / chunks.length}% { opacity: 0;}
          `}
          keyframesMask={`
            ${100 / chunks.length - (100 / chunks.length / 2)}% { mask-position: center 10%; }
            ${100 / chunks.length}% { mask-position: center 200%; }
          `}
          key={index}
        >
          <img alt="" src={item.first()} />
          <div
            className="mask"
          >
            <img alt="" src={item.last()} />
          </div>
        </IndexPageBlockFadeRowStyled>;
      })}
      <IndexPageBlockButtonTextStyled>
        {props.title}
      </IndexPageBlockButtonTextStyled>
      <TouchEffect zIndex={10} />
    </IndexPageBlockButtonStyled>;
  };

  renderBlurWide = (props) => {
    if (!this.state.isLoaded) {
      return <IndexPageBlockButtonStyled
        type={this.props.block.type}
        effectAnimation={this.props.block.effect.animation}
        effectDirection={this.props.block.effect.direction}
        onClick={this.handleClick}
      ><Loader /></IndexPageBlockButtonStyled>;
    }

    const speedAnim = window.clientConfig.isWebDesktop ? "2" : "3";
    
    return <IndexPageBlurBlockButtonStyled
      type={this.props.block.type}
      effectAnimation={this.props.block.effect.animation}
      effectDirection={this.props.block.effect.direction}
      isDesktop={window.clientConfig.isWebDesktop}
      onClick={this.handleClick}
    >
      {this.props.block.extra.is_pro && <p className="pro-label">Pro</p>}
      {props.assetsUrls.map((item, index) => {
        return <IndexPageBlockBlurRowStyled
          className="blur-row"
          style={{"--animation-duration": `${props.assetsUrls.length * speedAnim}s`, "--animation-delay": `${index * speedAnim}s`}}
          keyframes={`
            0.1% { 
              opacity: 1; 
            }

            ${100 / props.assetsUrls.length / 10}% { 
              filter: blur(0); 
              opacity: 1; 
            }

            ${100 / props.assetsUrls.length}% { 
              filter: blur(0); 
              opacity: 1; 
            }

            ${100 / props.assetsUrls.length + 0.001}% { 
              filter: blur(10px); 
              opacity: 0; 
            }
          `}
          key={index}
        >
          <img alt="" src={item} />
        </IndexPageBlockBlurRowStyled>;
      })}
      <IndexPageBlockButtonTextStyled>
        {props.title}
      </IndexPageBlockButtonTextStyled>
      <TouchEffect zIndex={10} />
    </IndexPageBlurBlockButtonStyled>
  }

  renderShiftWide = (props) => {
    if (!this.state.isLoaded) {
      return <IndexPageBlockButtonStyled
        type={this.props.block.type}
        effectAnimation={this.props.block.effect.animation}
        effectDirection={this.props.block.effect.direction}
        onClick={this.handleClick}
      ><Loader /></IndexPageBlockButtonStyled>;
    }

    const chunkSize = 2;
    const chunks = [];
    for (let i = 0; i < props.assetsUrls.length; i += chunkSize) {
      chunks.push(props.assetsUrls.slice(i, i + chunkSize));
    }

    const durationAnimValue = window.clientConfig.isWebDesktop ? "1" : "1.5";
    const delayAnimValue = window.clientConfig.isWebDesktop ? "2" : "3";

    return <IndexPageBlockButtonStyled
      type={this.props.block.type}
      effectAnimation={this.props.block.effect.animation}
      effectDirection={this.props.block.effect.direction}
      isDesktop={window.clientConfig.isWebDesktop}
      onClick={this.handleClick}
    >
      {this.props.block.extra.is_pro && <p className="pro-label">Pro</p>}
      {chunks.map((item, index) => {
        return <IndexPageBlockShiftRowStyled
          className="shift-row"
          style={{"--animation-duration": `${props.assetsUrls.length * durationAnimValue}s`, "--animation-delay": `${index * delayAnimValue}s`}}
          keyframes={`
            ${100 / props.assetsUrls.length / 5}% { 
              transform: translateX(0);
              opacity: 1;
              filter: blur(0); 
            }

            ${100 / props.assetsUrls.length}% { 
              transform: translateX(0);
              opacity: 1;
              filter: blur(0); 
            }

            ${100 / props.assetsUrls.length + (100 / props.assetsUrls.length - 100 / props.assetsUrls.length / 5)}% { 
              transform: translateX(0);
              opacity: 1;
              filter: blur(0); 
            }

            ${100 / props.assetsUrls.length + (100 / props.assetsUrls.length - 100 / props.assetsUrls.length / 5) + (100 / props.assetsUrls.length / 5) * 2}% { 
              transform: translateX(0);
              opacity: 0;
              filter: brightness(50%);   
            }
          `}
          key={index}
        >
          <img alt="" src={item[0]} />
          <img alt="" src={item[1]} />
        </IndexPageBlockShiftRowStyled>;
      })}
      <IndexPageBlockButtonTextStyled>
        {props.title}
      </IndexPageBlockButtonTextStyled>
      <TouchEffect zIndex={10} />
    </IndexPageBlockButtonStyled>;
  };

  renderFadeDefault = (props) => {
    if (!this.state.isLoaded) {
      return <IndexPageBlockButtonStyled
        type={this.props.block.type}
        effectAnimation={this.props.block.effect.animation}
        effectDirection={this.props.block.effect.direction}
        onClick={this.handleClick}
      ><Loader /></IndexPageBlockButtonStyled>;
    }

    return <IndexPageFadeBlockButtonStyled
      className={this.props.className}
      type={this.props.block.type}
      effectAnimation={this.props.block.effect.animation}
      effectDirection={this.props.block.effect.direction}
      onClick={this.handleClick}
    >
      {this.props.block.extra.is_pro && <p className="pro-label">Pro</p>}
      {props.assetsUrls.length > 1 && props.assetsUrls.map((url, index) => (
        <IndexPageBlockButtonObjectStyled
          style={{
            backgroundImage: `url(${url})`,
            animationDelay: `${index * .75}s`,
            animationDuration: `${props.assetsUrls.length * .75}s`
          }}
          keyframes={`
            ${100 / props.assetsUrls.length - 0.01}% { opacity: 1;}
            ${100 / props.assetsUrls.length}% { opacity: 0;}
          `}
          className={this.props.block.type}
          vertical={this.props.block.effect.direction !== "vertical"}
          toggle={this.props.block.effect.direction === "toggle"}
          fade={this.props.block.effect.animation === "fade"}
          isDesktop={window.clientConfig.isWebDesktop}
          hidden={this.props.block.type === "wide" && this.props.block.effect.direction === "vertical"}
          key={url}
        />
      ))}
      <IndexPageBlockButtonTextStyled>
        {props.title}
      </IndexPageBlockButtonTextStyled>
      <TouchEffect zIndex={10} />
    </IndexPageFadeBlockButtonStyled>;
  };

  renderDefault = (props) => {
    if (!this.state.isLoaded) {
      return <IndexPageBlockButtonStyled
        type={this.props.block.type}
        effectAnimation={this.props.block.effect.animation}
        effectDirection={this.props.block.effect.direction}
        onClick={this.handleClick}
      ><Loader /></IndexPageBlockButtonStyled>;
    }

    return <IndexPageBlockButtonStyled
      className={this.props.className}
      style={{
        backgroundImage: props.assetsUrls.isNotEmpty() ? `url(${props.assetsUrls.first()})` : null,
      }}
      type={this.props.block.type}
      effectAnimation={this.props.block.effect.animation}
      effectDirection={this.props.block.effect.direction}
      onClick={this.handleClick}
    >
      {this.props.block.extra.is_pro && <p className="pro-label">Pro</p>}
      {props.assetsUrls.length > 1 && <IndexPageBlockButtonObjectStyled
        style={{
          backgroundImage: `url(${props.assetsUrls.last()})`
        }}
        className={this.props.block.type}
        vertical={this.props.block.effect.direction !== "vertical"}
        fade={this.props.block.effect.animation === "fade"}
        hidden={this.props.block.type === "wide" && this.props.block.effect.direction === "vertical"}
        isDesktop={window.clientConfig.isWebDesktop}
      />}
      <IndexPageBlockButtonTextStyled>
        {props.title}
      </IndexPageBlockButtonTextStyled>
      <TouchEffect zIndex={10} />
    </IndexPageBlockButtonStyled>;
  };
}

BlockView.propTypes = {
  block: PropTypes.object.isRequired,
  position: PropTypes.number,
  onClick: PropTypes.func,
};
